import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineWrench,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark
} from 'react-icons/hi2';
import { authService } from '../../../services';
import masterDataService from '../../../services/masterDataService';
import SharedModal from '../../../components/common/SharedModal';
import PageLoading from "../../../components/common/PageLoading";
import "../PlannedMaintenanceMasters.css";

const ToolsPage = () => {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [formData, setFormData] = useState({
    toolName: '',
    toolCode: '',
    calibrationRequired: false,
    calibrationFreqDays: '',
    locationId: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      loadTools();
    } else {
      setError('Please login to view tools');
      setLoading(false);
    }
  }, [tenantId]);

  const loadTools = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await masterDataService.getTools(tenantId);
      setTools(data || []);
    } catch (err) {
      setError('Failed to load tools. Please try again.');
      console.error('Error loading tools:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredTools = tools.filter((tool) =>
    tool.toolName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tool.toolCode?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingTool(null);
    setFormData({
      toolName: '',
      toolCode: '',
      calibrationRequired: false,
      calibrationFreqDays: '',
      locationId: '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (tool) => {
    setEditingTool(tool);
    setFormData({
      toolName: tool.toolName || '',
      toolCode: tool.toolCode || '',
      calibrationRequired: tool.calibrationRequired || false,
      calibrationFreqDays: tool.calibrationFreqDays?.toString() || '',
      locationId: tool.locationId?.toString() || '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      await masterDataService.deleteTool(id);
      await loadTools();
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete tool. Please try again.');
      console.error('Error deleting tool:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.toolName.trim()) {
      errors.toolName = 'Tool name is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const toolData = {
        tenantId: tenantId,
        toolName: formData.toolName.trim(),
        toolCode: formData.toolCode.trim() || null,
        calibrationRequired: formData.calibrationRequired,
        calibrationFreqDays: formData.calibrationFreqDays ? parseInt(formData.calibrationFreqDays) : null,
        locationId: formData.locationId ? parseInt(formData.locationId) : null,
      };

      if (editingTool) {
        toolData.toolId = editingTool.toolId;
        await masterDataService.updateTool(editingTool.toolId, toolData);
      } else {
        await masterDataService.createTool(toolData);
      }

      await loadTools();
      setShowModal(false);
      setFormData({ toolName: '', toolCode: '', calibrationRequired: false, calibrationFreqDays: '', locationId: '' });
      setEditingTool(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors || 
                          err.message || 
                          'Failed to save tool. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTool(null);
    setFormData({ toolName: '', toolCode: '', calibrationRequired: false, calibrationFreqDays: '', locationId: '' });
    setFormErrors({});
  };

  if (loading) {
    return (
      <div className="pm-masters-page">
        <PageLoading message="Loading tools..." />
      </div>
    );
  }

  return (
    <div className="pagecontainer pm-masters-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Tools</h1>
          <p>Manage tools for Planned Maintenance</p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus />
          Add Tool
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search tools..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="table-info">
            {filteredTools.length} {filteredTools.length === 1 ? 'tool' : 'tools'}
          </div>
        </div>

        {filteredTools.length === 0 ? (
          <div className="empty-state">
            <HiOutlineWrench className="empty-icon" />
            <h3>No tools found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first tool'}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                Add Tool
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Tool Code</th>
                <th>Tool Name</th>
                <th>Calibration Required</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTools.map((tool) => (
                <tr key={tool.toolId}>
                  <td>{tool.toolId}</td>
                  <td>{tool.toolCode || '-'}</td>
                  <td className="name-cell">{tool.toolName}</td>
                  <td>{tool.calibrationRequired ? 'Yes' : 'No'}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(tool)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                      <button
                        className="btn-icon-delete"
                        onClick={() => setDeleteConfirm(tool)}
                        title="Delete"
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingTool ? 'Edit Tool' : 'Add New Tool'}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="tool-form"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting
                ? editingTool
                  ? 'Updating...'
                  : 'Creating...'
                : editingTool
                ? 'Update'
                : 'Create'}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="tool-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="toolName">
                Tool Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="toolName"
                value={formData.toolName}
                onChange={(e) =>
                  setFormData({ ...formData, toolName: e.target.value })
                }
                className={formErrors.toolName ? 'error' : ''}
                placeholder="Enter tool name"
              />
              {formErrors.toolName && (
                <span className="field-error">{formErrors.toolName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="toolCode">Tool Code</label>
              <input
                type="text"
                id="toolCode"
                value={formData.toolCode}
                onChange={(e) =>
                  setFormData({ ...formData, toolCode: e.target.value })
                }
                placeholder="Enter tool code (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="calibrationRequired" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  id="calibrationRequired"
                  checked={formData.calibrationRequired}
                  onChange={(e) =>
                    setFormData({ ...formData, calibrationRequired: e.target.checked })
                  }
                  style={{ width: 'auto', minHeight: 'auto' }}
                />
                Calibration Required
              </label>
            </div>

            {formData.calibrationRequired && (
              <div className="form-group">
                <label htmlFor="calibrationFreqDays">Calibration Frequency (days)</label>
                <input
                  type="number"
                  id="calibrationFreqDays"
                  value={formData.calibrationFreqDays}
                  onChange={(e) =>
                    setFormData({ ...formData, calibrationFreqDays: e.target.value })
                  }
                  placeholder="Enter calibration frequency in days"
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="locationId">Location ID</label>
              <input
                type="number"
                id="locationId"
                value={formData.locationId}
                onChange={(e) =>
                  setFormData({ ...formData, locationId: e.target.value })
                }
                placeholder="Enter location ID (optional)"
              />
            </div>
          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customClass="small"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={() => handleDelete(deleteConfirm.toolId)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete{' '}
            <strong>{deleteConfirm?.toolName}</strong>?
          </p>
          <p className="warning-text">
            This action cannot be undone.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default ToolsPage;
